import { type CSSProperties, type Ref, type SVGProps } from "react";

import type { LucideIcon } from "lucide-react";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import { BrandIcon } from "../brand-icon";
import type { UISize } from "../utils/UIContext";
import { type UiColor } from "../utils/colors";
import { ICON_SIZE_PX } from "../utils/iconSize";
import { type UiPaletteKey, resolveUiPalette } from "../utils/uiPalette";

export type IconSize = UISize;

export { ICON_SIZE_PX };

export type IconData = LucideIcon | BrandIcon | null;

export type IconProps = {
  icon?: IconData;
  color?: UiColor;
  size?: IconSize;
  className?: string;
  style?: CSSProperties;
  ref?: Ref<SVGSVGElement>;
} & Omit<SVGProps<SVGSVGElement>, "color" | "children" | "ref">;

const IconComponent = ({
  icon,
  color,
  size = "medium",
  className,
  style,
  ref,
  ...rest
}: IconProps) => {
  const theme = useTheme();
  if (!icon) {
    return null;
  }
  const uiPalette = color
    ? resolveUiPalette(theme, color as UiPaletteKey)
    : undefined;
  const iconColor = uiPalette?.ink || "currentColor";

  const px = ICON_SIZE_PX[size];
  const IconCls = icon;

  // CSS uses `stroke: currentColor`; set `color` so named colours
  // (e.g. green) win over the Lucide stroke presentation attribute.
  const colorStyle = iconColor !== "currentColor" ? { color: iconColor } : {};
  return (
    <IconCls
      {...rest}
      size={px}
      color={iconColor}
      strokeWidth={2.5}
      ref={ref}
      className={cx(className)}
      style={{ ...colorStyle, ...style }}
    />
  );
};

export const Icon = styled(IconComponent)`
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
  fill: none;
  stroke: currentColor;
  alignment-baseline: central;
  overflow: visible;

  @keyframes sdo-icon-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
